// A board, with its own game, its own Chessground and its own corner of the store.
//
// There used to be exactly one of each: a `game` exported from state/index.ts and a `ground` in
// state/live.ts. The small board on /lines already had to build its own pair by hand, and the tools
// being made room for — memory, a game against a sparring partner, blindfold — each need at least
// one more. So a board is a thing you make, not a global you assume.
//
// What lives where has not changed: the Chess and the Chessground are objects with identity and an
// imperative API, so they stay in `live`; everything about the position that is data goes to
// `boards.<id>` in the store, where components read it.

import { Chessground } from "#vendor/chessground/chessground.min.js";
import type { Api as ChessgroundApi, Piece } from "#vendor/chessground/chessground.min.js";
import { Chess } from "#vendor/chessjs/chess.js";
import type { Move } from "#vendor/chessjs/chess.js";
import { store } from "../state/index.ts";
import { live } from "../state/live.ts";
import { K } from "../state/keys.ts";
import {
    destsOf, fullmoveOf, legalUci, playUciMoves, positionState, turnColor, uciOf,
} from "../domain/boardPosition.ts";
import type { Side } from "../domain/boardPosition.ts";

/** What the board draws with. Chessground takes hex, so this repeats the five colours of tokens.css. */
export const BRUSH = {
    book: "#1A1A1A",        // --knight-black: what everyone plays here
    repertoire: "#1565C0",  // --digital-blue: what you said you would play
    engine: "#C62828",      // --fighter-red: what the engine wants
    hint: "#2E7D32",        // --success: the move you are being shown
    warning: "#B7791F",     // --verdict-inaccuracy, the amber of the verdict scale
} as const;

/**
 * What a board is allowed to show of the position it holds: the placement to paint, and whether the
 * last move may be marked on it.
 */
export interface BoardVeil {
    placement: (fen: string) => string;
    lastMove: boolean;
}

/** Who may move pieces: a side, "none" for nobody, null for whoever is to move (free play). */
export type MovableSide = Side | "none" | null;

/** A move made on the board, as everything downstream wants it. */
export interface BoardMove {
    from: string;
    to: string;
    san: string;
    uci: string;
    fenBefore: string;
    fenAfter: string;
    gameOver: boolean;
}

export interface BoardSessionOptions {
    /** The game to play on. Defaults to one of its own; the study passes the one legacy still shares. */
    chess?: Chess;
    fen?: string | null;
    orientation?: Side;
    movableSide?: MovableSide;
    /** Arrows and circles. The small board on /lines does not want them. */
    drawable?: boolean;
    /** A move the user made by dragging a piece. Programmatic moves do not come through here. */
    onUserMove?: (move: BoardMove) => void;
}

export class BoardSession {
    readonly id: string;
    readonly chess: Chess;
    private ground: ChessgroundApi | null = null;
    private movable: MovableSide;
    private orientation: Side;
    private readonly drawable: boolean;
    private readonly onUserMove?: (move: BoardMove) => void;
    /** Free placement: what is on the board stops coming from the game. See setEditable(). */
    private editable = false;
    private onPlacementChange?: (placement: string) => void;
    /** What the board is allowed to show of the position it holds. See setVeil(). */
    private veil: BoardVeil | null = null;

    constructor(id: string, options: BoardSessionOptions = {}) {
        this.id = id;
        this.chess = options.chess ?? new Chess();
        this.movable = options.movableSide ?? null;
        this.orientation = options.orientation ?? "white";
        this.drawable = options.drawable !== false;
        this.onUserMove = options.onUserMove;
        if (options.fen !== undefined) this.loadFen(options.fen);
        this.publish();
    }

    // -------------------------------------------------------------------------------------------
    // The board itself
    // -------------------------------------------------------------------------------------------

    /** Puts Chessground on an element. A session works without one: the game is the game. */
    attach(element: HTMLElement): void {
        this.ground = Chessground(element, {
            fen: this.chess.fen(),
            orientation: this.orientation,
            movable: {
                free: false,
                color: this.movableColor(),
                dests: destsOf(this.chess),
                events: { after: (from: string, to: string) => this.onDragged(from, to) },
            },
            premovable: { enabled: false },
            highlight: { lastMove: true, check: true },
            coordinates: true,
            // `enabled` is whether the user may draw; `visible` is whether anything drawn is painted. They
            // are not the same permission, and Chessground only builds its drawing layer when `visible` is
            // on at this moment: a board that starts invisible can never be drawn on afterwards, which is
            // how the way out of a journey and the squares of "light or dark" were being marked on nothing.
            drawable: { enabled: this.drawable, visible: true, shapes: [] },
            events: { change: () => this.onPlacementChanged() },
        });
        // A session may have been put in edit mode before it had a board to edit
        if (this.editable) this.applyEditMode();
    }

    detach(): void {
        this.ground?.destroy();
        this.ground = null;
    }

    /** The Chessground behind this board, for the few things only it can answer. */
    get api(): ChessgroundApi | null {
        return this.ground;
    }

    /** Chessground measures itself once; anything that changes the layout has to say so. */
    redraw(): void {
        this.ground?.redrawAll();
    }

    /** The arrows and circles drawn on it, with the brushes they are drawn with. */
    /**
     * What is drawn over the squares. Shapes are shown whenever there are any, even on a board
     * nobody is allowed to draw on: being told the way afterwards is not the same permission as
     * being able to scribble on it.
     */
    setShapes(shapes: unknown[], brushes: Record<string, unknown> = {}): void {
        this.ground?.set({ drawable: { brushes, shapes, visible: true } });
    }

    // -------------------------------------------------------------------------------------------
    // Who moves, and which way round
    // -------------------------------------------------------------------------------------------

    setMovableSide(side: MovableSide): void {
        this.movable = side;
        this.ground?.set({ movable: { color: this.movableColor(), dests: destsOf(this.chess) } });
        store.set(K.boards(this.id).movableSide, side);
    }

    setOrientation(side: Side): void {
        this.orientation = side;
        this.ground?.set({ orientation: side });
        store.set(K.boards(this.id).orientation, side);
    }

    flip(): Side {
        this.setOrientation(this.orientation === "white" ? "black" : "white");
        return this.orientation;
    }

    private movableColor(): Side | undefined {
        if (this.movable === "none") return undefined;
        const turn = turnColor(this.chess);
        if (!this.movable) return turn;
        return turn === this.movable ? this.movable : undefined;
    }

    // -------------------------------------------------------------------------------------------
    // Moving
    // -------------------------------------------------------------------------------------------

    /** A move played by hand on the board. Illegal ones are put back where they came from. */
    private onDragged(from: string, to: string): void {
        // While editing, a piece dragged across the board is a piece being placed, not a move: the
        // game is not what is on the board, and syncing from it would wipe what was put there.
        if (this.editable) return;
        const fenBefore = this.chess.fen();
        const legal = legalUci(this.chess, from + to);
        if (!legal) {
            this.sync();
            return;
        }
        const move = this.chess.move(legal.san) as Move;
        this.sync({ from, to });
        this.onUserMove?.({
            from, to, san: move.san, uci: uciOf(move),
            fenBefore, fenAfter: this.chess.fen(), gameOver: this.chess.isGameOver(),
        });
    }

    /** A move made for the player: a book reply, a line being replayed. SAN or UCI, either will do. */
    play(move: { san?: string; uci?: string }): boolean {
        const san = move.san || (move.uci ? legalUci(this.chess, move.uci)?.san : null);
        if (!san || !(this.chess.moves() as string[]).includes(san)) return false;
        const played = this.chess.move(san) as Move;
        this.sync({ from: played.from, to: played.to });
        return true;
    }

    // -------------------------------------------------------------------------------------------
    // Positions
    // -------------------------------------------------------------------------------------------

    /** The position the board starts from. Null is the initial one. */
    loadFen(fen: string | null): boolean {
        if (!fen) {
            this.chess.reset();
        } else {
            try {
                this.chess.load(fen);
            } catch (err) {
                return false;
            }
        }
        const base = this.chess.fen();
        store.batch((set) => {
            set(K.boards(this.id).baseFen, fen || null);
            set(K.boards(this.id).baseTurn, this.chess.turn());
            set(K.boards(this.id).baseFullmove, fullmoveOf(base));
        });
        this.sync();
        return true;
    }

    /** A line: where it starts, and the moves that lead to the position to play. */
    loadLine(fen: string | null, uciMoves: string | null): void {
        this.loadFen(fen);
        if (playUciMoves(this.chess, uciMoves)) this.sync();
    }

    /** Back to the position after `ply`, replaying a history that may be longer than the game's. */
    jumpToPly(targetPly: number, history: string[] = this.chess.history() as string[]): void {
        if (!history.length) return;
        const baseFen = store.get(K.boards(this.id).baseFen);
        if (baseFen) {
            this.chess.load(baseFen);
        } else {
            this.chess.reset();
        }
        for (let i = 0; i <= targetPly && i < history.length; i++) this.chess.move(history[i]!);
        this.sync();
    }

    /** The last move, undone: a move played by mistake, or one a game gives back. */
    undo(): boolean {
        const undone = this.chess.undo();
        if (undone) this.sync();
        return !!undone;
    }

    // -------------------------------------------------------------------------------------------
    // Editing: a position arranged by hand
    // -------------------------------------------------------------------------------------------

    /**
     * Free placement: pieces go wherever they are put, and the game stops being the truth about the
     * board. A position with pieces missing is not a legal one, so the memory games cannot express
     * what they ask for through a game — they ask for this instead. The study never does.
     *
     * The game is left alone while it lasts: it still holds the position the round is about, which
     * is what the placement is then checked against.
     */
    setEditable(on: boolean, onChange?: (placement: string) => void): void {
        this.editable = on;
        this.onPlacementChange = onChange;
        if (on) {
            this.applyEditMode();
        } else {
            this.ground?.set({ draggable: { deleteOnDropOff: false } });
            this.sync();
        }
    }

    private applyEditMode(): void {
        this.ground?.set({
            movable: { free: true, color: "both", dests: undefined },
            draggable: { deleteOnDropOff: true },
            lastMove: undefined,
        });
    }

    /**
     * What the board shows of the position it holds, and whether the last move is marked with it.
     *
     * The game underneath is untouched — it is still the whole truth, and still what a move is
     * checked against — but what is painted is only the part the tool allows. Playing blindfold is
     * this with almost nothing allowed; null is the board telling the truth, which is the default.
     */
    setVeil(veil: BoardVeil | null): void {
        this.veil = veil;
        this.sync();
    }

    /** A position painted on the board without the game following it. Edit mode only. */
    showFen(fen: string): void {
        this.ground?.set({ fen, lastMove: undefined });
    }

    /** What is on the board now, as the placement field of a FEN. Null without a board to read. */
    placement(): string | null {
        return this.ground?.getFen() ?? null;
    }

    /** A piece being dragged onto the board from somewhere that is not the board. */
    dragNewPiece(piece: Piece, event: MouseEvent | TouchEvent): void {
        this.ground?.dragNewPiece(piece, event, true);
    }

    private onPlacementChanged(): void {
        if (this.editable) this.onPlacementChange?.(this.placement() ?? "");
    }

    // -------------------------------------------------------------------------------------------
    // Publishing
    // -------------------------------------------------------------------------------------------

    /** Pushes the position onto the board and into the store. */
    sync(lastMove?: { from: string; to: string }): void {
        // The exception is edit mode, where the board is being arranged by hand: the game still
        // holds the position the round is about, and painting it would undo the player's work.
        if (this.editable) {
            this.publish(lastMove);
            return;
        }
        const shown = this.veil ? this.veil.placement(this.chess.fen()) : this.chess.fen();
        this.ground?.set({
            fen: shown,
            turnColor: turnColor(this.chess),
            movable: { free: false, color: this.movableColor(), dests: destsOf(this.chess) },
            lastMove: lastMove && (!this.veil || this.veil.lastMove) ? [lastMove.from, lastMove.to] : undefined,
        });
        this.publish(lastMove);
    }

    private publish(lastMove?: { from: string; to: string }): void {
        const state = positionState(this.chess);
        const keys = K.boards(this.id);
        store.batch((set) => {
            set(keys.fen, state.fen);
            set(keys.turn, state.turn);
            set(keys.history, state.history);
            set(keys.gameOver, state.gameOver);
            set(keys.orientation, this.orientation);
            set(keys.movableSide, this.movable);
            if (lastMove) set(keys.lastMove, [lastMove.from, lastMove.to]);
        });
    }

    dispose(): void {
        this.detach();
        live.boards.delete(this.id);
    }
}

/**
 * The board called `id`, made if it does not exist yet. Asking twice gives the same board: a page
 * that mounts its component again must not end up with two games under one name.
 */
export function boardSession(id: string, options: BoardSessionOptions = {}): BoardSession {
    const existing = live.boards.get(id);
    if (existing) return existing;
    const session = new BoardSession(id, options);
    live.boards.set(id, session);
    return session;
}

export function findBoard(id: string): BoardSession | null {
    return live.boards.get(id) ?? null;
}
